import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Download, Navigation } from 'lucide-react';
import { Field, Honeypot } from '../ui/Field';
import { ErrorSummary, type FieldError } from '../ui/ErrorSummary';
import { CONTACT, FORM_RESPONSE_TIME } from '../../data/site';
import type { Event } from '../../types/event';

/** Per person, chosen from the menu at check-in. Illustrative. */
const MEAL_PRICE = 13.95;
interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  bikeModel: string;
  bikeReg: string;
}
const EMPTY_FORM: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  bikeModel: '',
  bikeReg: ''
};

/**
 * F01. Event booking and interest registration, as a section of the event
 * page rather than a dialog.
 *
 * On success the form is replaced in place by its confirmation and focus
 * moves to the result heading. There is no route change, because a
 * confirmation that cannot be reloaded or shared should not have a URL of
 * its own.
 *
 * Submits nothing. Registry F01 and I02.
 */
export function BookingForm({
  event
}: {
  event: Event;
}) {
  const isCommunity = event.type === 'community';
  const [ticketCount, setTicketCount] = useState(1);
  /**
   * `spotsLeft` is optional on Event, because walk-in events publish no
   * capacity (D46). This form only renders for bookable ones, so the
   * fallback is a floor rather than a real limit.
   */
  const maxPlaces = event.spotsLeft ?? 1;
  const [includeMeal, setIncludeMeal] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const confirmationRef = useRef<HTMLHeadingElement | null>(null);
  const totalPrice = useMemo(() => {
    if (event.price === undefined) return 0;
    const meals = includeMeal ? MEAL_PRICE * ticketCount : 0;
    return event.price * ticketCount + meals;
  }, [event.price, includeMeal, ticketCount]);
  const validate = (): FieldError[] => {
    const found: FieldError[] = [];
    if (!form.firstName.trim()) {
      found.push({
        fieldId: 'booking-firstName',
        message: 'Enter your first name'
      });
    }
    if (!form.lastName.trim()) {
      found.push({
        fieldId: 'booking-lastName',
        message: 'Enter your last name'
      });
    }
    if (!form.email.trim()) {
      found.push({
        fieldId: 'booking-email',
        message: 'Enter an email address so we can send your confirmation'
      });
    } else if (!/.+@.+\..+/.test(form.email.trim())) {
      found.push({
        fieldId: 'booking-email',
        message: 'Enter an email address in the format name@example.com'
      });
    }
    if (!form.phone.trim()) {
      found.push({
        fieldId: 'booking-phone',
        message: 'Enter a telephone number in case we need to reach you on the day'
      });
    }
    if (!isCommunity && !form.bikeModel.trim()) {
      found.push({
        fieldId: 'booking-bikeModel',
        message: 'Enter the make and model of your bike'
      });
    }
    if (!isCommunity && !form.bikeReg.trim()) {
      found.push({
        fieldId: 'booking-bikeReg',
        message: 'Enter your registration so we can manage parking'
      });
    }
    return found;
  };
  const errorFor = (fieldId: string) => errors.find(error => error.fieldId === fieldId)?.message;
  const handleSubmit = async (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault();
    const found = validate();
    setErrors(found);
    if (found.length > 0) return;
    setIsSubmitting(true);
    // Stands in for payment and submission, neither of which exist yet.
    await new Promise(resolve => window.setTimeout(resolve, 700));
    setIsSubmitting(false);
    setIsSent(true);
    requestAnimationFrame(() => confirmationRef.current?.focus({
      preventScroll: false
    }));
  };
  if (isSent) {
    return <div className="rounded-container border border-hairline bg-white p-6 sm:p-8" data-id="element-62" data-mp="3pn6r44d9r206">
        <span className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-full bg-moss/15" aria-hidden="true" data-id="element-63" data-mp="3to0gz3a9uqvp">
          <Check className="h-7 w-7 text-moss" data-id="element-64" data-mp="7i3gy58g3zsw" />
        </span>

        <h3 ref={confirmationRef} tabIndex={-1} className="font-bebas text-4xl leading-none tracking-wide text-ink focus:outline-none" data-id="element-65" data-mp="7hbylw3uzykk">
          You're on the list, {form.firstName.trim()}.
        </h3>

        <p className="mt-3 max-w-prose leading-relaxed text-ink/80" data-id="element-66" data-mp="3fy6qyhn160ke">
          {isCommunity ? 'Thanks for putting your name down for ' : 'Your place at '}
          <strong className="font-bold text-ink" data-id="element-67" data-mp="3kqganfxjy4e8">{event.title}</strong>,{' '}
          {event.when}.
        </p>

        <dl className="mt-6 space-y-4 rounded-container bg-cream p-5" data-id="element-68" data-mp="2s82o4cho24ug">
          <div data-id="element-69" data-mp="2s69ecobrb4i8">
            <dt className="text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-70" data-mp="2kotdyh1c624c">
              What happens next
            </dt>
            <dd className="mt-1 text-ink/80" data-id="element-71" data-mp="2fqrwg0jk6auz">
              We'll email {form.email.trim()} {FORM_RESPONSE_TIME} to confirm
              {event.price !== undefined ? ' your place and sort out payment.' : ' your place.'}
            </dd>
          </div>
          <div data-id="element-72" data-mp="21qh1alb7ua40">
            <dt className="text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-73" data-mp="21r0l67kff8ko">
              On the day
            </dt>
            <dd className="mt-1 text-ink/80" data-id="element-74" data-mp="1wx5uulabfvcm">
              Come along 15 minutes early so you can check in and park up
              {!isCommunity && form.bikeReg.trim() ? `, and bring registration ${form.bikeReg.trim()}.` : '.'}
              {includeMeal && ' You can pick your meal off the menu when you check in.'}
            </dd>
          </div>
        </dl>

        {event.route && <div className="mt-6 rounded-container border border-moss/25 bg-moss/10 p-5" data-id="element-75" data-mp="13erk9gcf2pga">
            <h4 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-76" data-mp="18e45xxrjxqha">
              <Navigation className="h-4 w-4 text-moss" aria-hidden="true" data-id="element-77" data-mp="uf4jmwrihnoa" />
              Download your route
            </h4>
            <p className="mt-1.5 text-sm text-ink-muted" data-id="element-78" data-mp="zeh2z7j6ccm4">
              Pop it onto your GPS or navigation app before the ride.
            </p>
            {/* Disabled buttons, not `href="#"` links. The files do not
                exist yet (I05), and a link to "#" reads to a screen reader
                as a working download, scrolls the page to the top on
                activation, and gives the keyboard user no clue that
                nothing happened. A disabled button announces its state. */}
            <div className="mt-3 flex flex-wrap gap-2" data-id="element-79" data-mp="260joz3zmmw6y">
              {['GPX file', 'ITN file for TomTom'].map(label => <button key={label} type="button" disabled className="inline-flex cursor-not-allowed items-center gap-2 rounded-container bg-moss px-4 py-2.5 text-xs font-bold text-white opacity-50" data-id="element-80" data-mp="2aunwboct168k">
                  <Download className="h-3.5 w-3.5" aria-hidden="true" data-id="element-81" data-mp="2tzk8n78m0u7q" />
                  {label}
                </button>)}
            </div>
            <p className="mt-2 text-sm text-ink-muted" data-id="element-82" data-mp="219tuwzaw3uc5">
              We're getting these files ready. Give us a ring in the meantime
              and we'll talk you through the route.
            </p>
          </div>}

        <div className="mt-6 border-t border-hairline pt-5" data-id="element-83" data-mp="2b89mf3dtx5m6">
          <Link to="/whats-on" className="text-sm font-bold text-flame underline decoration-flame/40 underline-offset-4 transition-colors duration-200 ease-out-expo hover:decoration-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-84" data-mp="267lw6jxfcst0">
            Back to what's on
          </Link>
        </div>
      </div>;
  }
  return <div className="rounded-container border border-hairline bg-white p-6 sm:p-8" data-id="element-85" data-mp="1jz6nz3tzt5wl">
      <h3 className="font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-86" data-mp="1oyjdxxg5ut3e">
        {isCommunity ? 'Register your interest' : 'Book your place'}
      </h3>
      <p className="mt-2 max-w-prose text-sm leading-relaxed text-ink/80" data-id="element-87" data-mp="1tquacqbozwr7">
        {isCommunity ? 'Free to attend, but let us know you are coming so we can set out enough space and tools.' : `We'll confirm by email ${FORM_RESPONSE_TIME}.`}
      </p>

      {/* noValidate so our messages replace the browser bubbles, which are
          announced inconsistently and vanish on blur. */}
      <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-5" data-id="element-88" data-mp="2cnccm1o63f1p">
        <ErrorSummary errors={errors} data-id="element-89" data-mp="dc2rb40fs1uk" />
        <Honeypot data-id="element-90" data-mp="dd43jbt6uudv" />

        {!isCommunity && <fieldset data-id="element-91" data-mp="ib5ok2a6d3rz">
            <legend className="mb-2 text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-92" data-mp="iamgree8nvro">
              How many riders
            </legend>
            <div className="flex items-center gap-3" data-id="element-93" data-mp="604wslso25nv">
              <button type="button" onClick={() => setTicketCount(count => Math.max(1, count - 1))} disabled={ticketCount <= 1} aria-label="One fewer rider" className="inline-flex h-11 w-11 items-center justify-center rounded-container border border-control text-lg font-bold text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2 disabled:opacity-40" data-id="element-94" data-mp="5yk3ezi38srl">
                <span aria-hidden="true" data-id="element-95" data-mp="fqg50kjdv10k">−</span>
              </button>
              <output aria-live="polite" className="w-12 text-center font-bebas text-3xl leading-none tracking-wide text-ink" data-id="element-96" data-mp="34h8vt8z0jyp7">
                {ticketCount}
              </output>
              <button type="button" onClick={() => setTicketCount(count => Math.min(maxPlaces, count + 1))} disabled={ticketCount >= maxPlaces} aria-label="One more rider" className="inline-flex h-11 w-11 items-center justify-center rounded-container border border-control text-lg font-bold text-ink transition-colors duration-200 ease-out-expo hover:bg-ink/5 focus:outline-none focus:ring-2 focus:ring-moss focus:ring-offset-2 disabled:opacity-40" data-id="element-97" data-mp="wl7ti6yo79hv">
                <span aria-hidden="true" data-id="element-98" data-mp="1pb6qqpn0qcxm">+</span>
              </button>
            </div>
          </fieldset>}

        {event.mealIncluded && <label className="flex cursor-pointer items-start gap-3" data-id="element-99" data-mp="1kcd8kjwsdxr4">
            <input type="checkbox" checked={includeMeal} onChange={changeEvent => setIncludeMeal(changeEvent.target.checked)} className="mt-0.5 h-5 w-5 shrink-0 rounded-control border-control text-flame focus:ring-2 focus:ring-moss focus:ring-offset-2" data-id="element-100" data-mp="3r32z5qdw36o6" />
            <span data-id="element-101" data-mp="1sskmceweq5s0">
              <span className="block text-sm font-bold text-ink" data-id="element-102" data-mp="1st48y8x05icd">
                Add a meal on your return (+£{MEAL_PRICE.toFixed(2)} each)
              </span>
              <span className="block text-sm text-ink-muted" data-id="element-103" data-mp="1ws3bjmv9h30n">
                You choose from the menu when you check in.
              </span>
            </span>
          </label>}

        {event.price !== undefined && <div className="flex items-center justify-between rounded-container bg-cream px-4 py-3" data-id="element-104" data-mp="19u02n7wui2oe">
            <span className="text-xs font-bold uppercase tracking-[0.12em] text-ink-muted" data-id="element-105" data-mp="1dus9xvm5pxi1">
              Total
            </span>
            <output className="font-bebas text-2xl leading-none tracking-wide text-flame" data-id="element-106" data-mp="3492moq34ksjx">
              £{totalPrice.toFixed(2)}
            </output>
          </div>}

        <div className="grid gap-4 sm:grid-cols-2" data-id="element-107" data-mp="l1f2x78ku408">
          <Field id="booking-firstName" label="First name" required autoComplete="given-name" value={form.firstName} onChange={value => setForm(prev => ({
          ...prev,
          firstName: value
        }))} error={errorFor('booking-firstName')} data-id="element-108" data-mp="kyss8yqe38ds" />
          <Field id="booking-lastName" label="Last name" required autoComplete="family-name" value={form.lastName} onChange={value => setForm(prev => ({
          ...prev,
          lastName: value
        }))} error={errorFor('booking-lastName')} data-id="element-109" data-mp="13rn0l9tic291" />
        </div>

        <div className="grid gap-4 sm:grid-cols-2" data-id="element-110" data-mp="py5oub72bttk">
          <Field id="booking-email" label="Email" required type="email" inputMode="email" autoComplete="email" value={form.email} onChange={value => setForm(prev => ({
          ...prev,
          email: value
        }))} error={errorFor('booking-email')} data-id="element-111" data-mp="1ng5u23ljq1tj" />
          <Field id="booking-phone" label="Telephone" required type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={value => setForm(prev => ({
          ...prev,
          phone: value
        }))} error={errorFor('booking-phone')} data-id="element-112" data-mp="1zxzub5nez7ud" />
        </div>

        {!isCommunity && <div className="grid gap-4 sm:grid-cols-2" data-id="element-113" data-mp="2iohicwxaxf9e">
            <Field id="booking-bikeModel" label="Bike make and model" required value={form.bikeModel} onChange={value => setForm(prev => ({
          ...prev,
          bikeModel: value
        }))} error={errorFor('booking-bikeModel')} data-id="element-114" data-mp="2ipskytf0ggyx" />
            <Field id="booking-bikeReg" label="Registration" required hint="Used for parking management and security." value={form.bikeReg} onChange={value => setForm(prev => ({
          ...prev,
          bikeReg: value.toUpperCase()
        }))} error={errorFor('booking-bikeReg')} data-id="element-115" data-mp="2wlwfkntsqrbx" />
          </div>}

        <button type="submit" disabled={isSubmitting} aria-busy={isSubmitting} className="w-full rounded-container bg-flame px-6 py-3.5 text-sm font-black uppercase tracking-widest text-white transition-colors duration-200 ease-out-expo hover:bg-flame-dark focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70" data-id="element-116" data-mp="204qor2oqb0wp">
          {isSubmitting ? 'Sending…' : isCommunity ? 'Submit interest' : event.price !== undefined ? 'Proceed to payment' : 'Confirm reservation'}
        </button>

        {/* Transparency at the point of collection, which is where UK GDPR
            Article 13 expects it rather than only in the footer. The
            registration gets its own mention because a rider is entitled to
            know why a café is asking for it. */}
        <p className="text-sm leading-relaxed text-ink-muted" data-id="element-117" data-mp="1lwvshhjef1ex">
          We use these details to hold your place
          {event.route ? ', and your registration to marshal the car park' : ''}
          . See our{' '}
          <Link to="/legal/privacy" className="font-bold text-flame underline decoration-flame/40 underline-offset-2 hover:decoration-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-118" data-mp="1c6ja9vz23kby">
            privacy policy
          </Link>
          {event.price !== undefined ? <>
              {' '}
              and{' '}
              <Link to="/legal/terms" className="font-bold text-flame underline decoration-flame/40 underline-offset-2 hover:decoration-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-119" data-mp="2jhnmgdoyw3qi">
                terms
              </Link>
              , including how cancellations work
            </> : null}
          .
        </p>

        {/* A telephone route alongside the form, always. */}
        <p className="text-center text-sm text-ink-muted" data-id="element-120" data-mp="1vte3rkn1vw3l">
          Or ring us on{' '}
          <a href={CONTACT.telephoneHref} className="font-bold text-flame underline decoration-flame/40 underline-offset-2 hover:decoration-flame focus:outline-none focus:ring-2 focus:ring-flame focus:ring-offset-2" data-id="element-121" data-mp="1qu18qcm0zipx">
            {CONTACT.telephone}
          </a>
        </p>
      </form>
    </div>;
}